import { useCallback } from "react";
import type {
  AdvisorMovement,
  Client,
  Encargo,
  Expense,
  InventoryItem,
  Loan,
  Product,
  Promotion,
  Receivable,
  RestockItem,
  Sale,
  Settings,
  StockEntry,
} from "@/types";
import type { StoreCtx } from "@/store/appTypes";
import {
  removeAdvisorMovement as apiRemoveAdvisorMovement,
  removeClient,
  removeEncargo,
  removeExpense as apiRemoveExpense,
  removeInventory,
  removeLoan as apiRemoveLoan,
  removeProduct,
  removePromotion,
  removeReceivable as apiRemoveReceivable,
  removeRestockItem,
  removeSale,
  removeStockEntry as apiRemoveStockEntry,
  saveAdvisorMovement,
  saveClient,
  saveEncargo,
  saveExpense,
  saveInventory,
  saveLoan,
  saveProduct,
  savePromotion,
  saveReceivable,
  saveRestockItem,
  saveSale,
  saveSettings,
  saveStockEntry,
} from "@/store/api";
import { clearOperationalData, seedDemoData } from "@/store/seed";
import {
  BACKUP_TABLES,
  buildBackupPayload,
  buildSignature,
  backupFileName,
  downloadJson,
  isRequiredFilled,
  type BackupKey,
  type BackupPayload,
  type RestoreSummary,
  type RestoreSummaryRow,
} from "@/utils/jsonBackup";

type AnyItem = Record<string, unknown>;

// Respaldo y borrado: descarga del respaldo JSON, restauración selectiva no
// destructiva, demostración y borrado selectivo por módulo.
export const useBackupActions = (ctx: StoreCtx) => {
  const { data, setData, setError, run, refresh } = ctx;

  // Descarga el respaldo completo de las tablas registradas.
  const exportBackup = useCallback((): BackupPayload => {
    const payload = buildBackupPayload(data);
    downloadJson(payload, backupFileName());
    return payload;
  }, [data]);

  // Restaura SOLO las categorías seleccionadas. Nunca borra ni sobrescribe:
  // agrega únicamente los registros que aún no existen (dedupe por firma).
  const restoreBackup = useCallback(
    async (payload: BackupPayload, selection: BackupKey[]): Promise<RestoreSummary | null> => {
      if (selection.length === 0) {
        setError("No seleccionaste ningún módulo para restaurar.");
        return null;
      }
      setError(null);
      const rows: RestoreSummaryRow[] = [];

      try {
        // Mapa de ids de producto: primero los existentes (por SKU), luego los
        // que se vayan creando, para remapear las referencias de otras tablas.
        const idMap = new Map<string, string>();
        for (const p of data.products) idMap.set(p.id, p.id);
        const skuToId = new Map<string, string>();
        for (const p of data.products) skuToId.set((p.sku || "").toLowerCase(), p.id);
        const payloadProducts = (payload.data.products as Product[] | undefined) ?? [];
        for (const p of payloadProducts) {
          const existing = skuToId.get((p.sku || "").toLowerCase());
          if (existing) idMap.set(p.id, existing);
        }
        const remap = (productId: string): string => idMap.get(productId) ?? productId;

        const insertFor = async (key: BackupKey, item: AnyItem): Promise<unknown> => {
          const base: AnyItem = { ...item, id: "" };
          if (typeof base.productId === "string") base.productId = remap(base.productId);
          switch (key) {
            case "products":
              return saveProduct(base as unknown as Product);
            case "clients":
              return saveClient(base as unknown as Client);
            case "inventory":
              return saveInventory(base as unknown as InventoryItem);
            case "sales":
              return saveSale(base as unknown as Sale);
            case "expenses":
              return saveExpense(base as unknown as Expense);
            case "receivables":
              return saveReceivable(base as unknown as Receivable);
            case "loans":
              return saveLoan(base as unknown as Loan);
            case "promotions":
              return savePromotion(base as unknown as Promotion);
            case "encargos":
              return saveEncargo(base as unknown as Encargo);
            case "advisorMovements":
              return saveAdvisorMovement(base as unknown as AdvisorMovement);
            case "restockItems":
              return saveRestockItem(base as unknown as RestockItem);
            case "stockEntries":
              return saveStockEntry(base as unknown as StockEntry);
            default:
              return null;
          }
        };

        const ordered = BACKUP_TABLES.filter((def) => selection.includes(def.key));

        for (const def of ordered) {
          // Configuración: se restaura como objeto único si difiere del actual.
          if (def.kind === "settings") {
            const incoming = payload.data[def.key];
            if (!incoming) continue;
            const same = JSON.stringify(incoming) === JSON.stringify(data.settings);
            if (same) {
              rows.push({ key: def.key, label: def.label, inserted: 0, duplicates: 1, skipped: 0 });
              continue;
            }
            try {
              await saveSettings(incoming as Settings);
              rows.push({ key: def.key, label: def.label, inserted: 1, duplicates: 0, skipped: 0 });
            } catch (e) {
              rows.push({
                key: def.key,
                label: def.label,
                inserted: 0,
                duplicates: 0,
                skipped: 0,
                error: e instanceof Error ? e.message : "Error al restaurar la configuración.",
              });
            }
            continue;
          }

          const existingArr =
            ((data as unknown as Record<string, unknown>)[def.key] as AnyItem[]) ?? [];
          const signatures = new Set(existingArr.map((it) => buildSignature(it, def.signature)));
          const incoming = (payload.data[def.key] as AnyItem[] | undefined) ?? [];

          let inserted = 0;
          let duplicates = 0;
          let skipped = 0;
          let error = "";

          for (const rawItem of incoming) {
            if (!isRequiredFilled(rawItem, def.required)) {
              skipped += 1;
              continue;
            }
            const normalized: AnyItem = { ...rawItem };
            if (typeof normalized.productId === "string") {
              normalized.productId = remap(normalized.productId);
            }
            const signature = buildSignature(normalized, def.signature);
            if (signatures.has(signature)) {
              duplicates += 1;
              continue;
            }
            try {
              const saved = await insertFor(def.key, rawItem);
              if (
                def.key === "products" &&
                rawItem.id &&
                saved &&
                typeof saved === "object" &&
                "id" in saved
              ) {
                const newId = String((saved as { id: string }).id);
                idMap.set(String(rawItem.id), newId);
                skuToId.set(String((rawItem as Product).sku || "").toLowerCase(), newId);
              }
              signatures.add(signature);
              inserted += 1;
            } catch (e) {
              error = e instanceof Error ? e.message : "Error al insertar un registro.";
            }
          }
          rows.push({ key: def.key, label: def.label, inserted, duplicates, skipped, error });
        }

        await refresh();
        return rows.reduce<RestoreSummary>(
          (acc, r) => ({
            rows: [...acc.rows, r],
            inserted: acc.inserted + r.inserted,
            duplicates: acc.duplicates + r.duplicates,
            skipped: acc.skipped + r.skipped,
          }),
          { rows: [], inserted: 0, duplicates: 0, skipped: 0 }
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : "No se pudo restaurar el respaldo.");
        return null;
      }
    },
    [data, refresh, setError]
  );

  const loadDemo = useCallback(
    () =>
      run(async () => {
        await seedDemoData();
        await refresh();
      }),
    [run, refresh]
  );

  const clearAll = useCallback(
    () =>
      run(async () => {
        await clearOperationalData();
        await refresh();
      }),
    [run, refresh]
  );

  // Borrado selectivo por lote: elimina SOLO los ids elegidos de un módulo.
  // Es tolerante a fallos: si un registro está referenciado, se conserva y se
  // informa cuántos no se pudieron borrar.
  const deleteMany = useCallback(
    (module: import("@/types").DeletableModule, ids: string[]): Promise<number> =>
      (async () => {
        const unique = Array.from(new Set(ids.filter(Boolean)));
        if (unique.length === 0) return 0;
        const failed: string[] = [];
        for (const id of unique) {
          try {
            switch (module) {
              case "products":
                await removeProduct(id);
                break;
              case "inventory":
                await removeInventory(id);
                break;
              case "clients":
                await removeClient(id);
                break;
              case "sales":
                await removeSale(id);
                break;
              case "expenses":
                await apiRemoveExpense(id);
                break;
              case "receivables":
                await apiRemoveReceivable(id);
                break;
              case "loans":
                await apiRemoveLoan(id);
                break;
              case "promotions":
                await removePromotion(id);
                break;
              case "encargos":
                await removeEncargo(id);
                break;
              case "movements":
                await apiRemoveAdvisorMovement(id);
                break;
              case "restock":
                await removeRestockItem(id);
                break;
              case "stockEntries":
                await apiRemoveStockEntry(id);
                break;
              default:
                break;
            }
          } catch {
            failed.push(id);
          }
        }
        const failedSet = new Set(failed);
        const removed = new Set(unique.filter((id) => !failedSet.has(id)));
        setData((p) => {
          const next = { ...p };
          switch (module) {
            case "products":
              next.products = p.products.filter((x) => !removed.has(x.id));
              next.inventory = p.inventory.filter((x) => !removed.has(x.productId));
              break;
            case "inventory":
              next.inventory = p.inventory.filter((x) => !removed.has(x.productId));
              break;
            case "clients":
              next.clients = p.clients.filter((x) => !removed.has(x.id));
              break;
            case "sales":
              next.sales = p.sales.filter((x) => !removed.has(x.id));
              break;
            case "expenses":
              next.expenses = p.expenses.filter((x) => !removed.has(x.id));
              break;
            case "receivables":
              next.receivables = p.receivables.filter((x) => !removed.has(x.id));
              break;
            case "loans":
              next.loans = p.loans.filter((x) => !removed.has(x.id));
              break;
            case "promotions":
              next.promotions = p.promotions.filter((x) => !removed.has(x.id));
              break;
            case "encargos":
              next.encargos = p.encargos.filter((x) => !removed.has(x.id));
              break;
            case "movements":
              next.advisorMovements = p.advisorMovements.filter((x) => !removed.has(x.id));
              break;
            case "restock":
              next.restockItems = p.restockItems.filter((x) => !removed.has(x.id));
              break;
            case "stockEntries":
              next.stockEntries = p.stockEntries.filter((x) => !removed.has(x.id));
              break;
            default:
              break;
          }
          return next;
        });
        if (failed.length > 0) {
          setError(
            `${removed.size} eliminado(s); ${failed.length} no se pudieron borrar (están en uso).`
          );
        } else {
          setError(null);
        }
        await refresh();
        return removed.size;
      })(),
    [refresh, setData, setError]
  );

  return { exportBackup, restoreBackup, loadDemo, clearAll, deleteMany };
};